import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Download, RefreshCw } from 'lucide-react';
import { getPedido, PedidoDetalhe, recordServiceLevelActivity } from '../api';
import {trackServiceLevelOutcome} from '../analytics';
import { downloadCsv, fmtDataHora, fmtDataHoraFoto, fmtDias, fmtMoney2, fmtNum, fmtPct } from '../utils/format';

export default function PedidoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const voltar = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/service-level/dashboard');
  };
  const [data, setData] = useState<PedidoDetalhe | null>(null);
  const [erro, setErro] = useState('');
  const [erroExportacao, setErroExportacao] = useState('');
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;
    setData(null);
    setErro('');
    setErroExportacao('');
    if (!id) { setErro('Pedido não informado. Volte ao fechamento e selecione um pedido.'); return; }
    getPedido(id)
      .then((result) => { if (ativo) setData(result); })
      .catch((e: Error) => { if (ativo) setErro(e.message); });
    return () => { ativo = false; };
  }, [id, tentativa]);

  if (erro) return <div className="card state-panel" role="alert"><h2>Não foi possível carregar o pedido</h2><p>{erro}</p><div className="filters"><button type="button" className="btn" onClick={() => setTentativa((n) => n + 1)}><RefreshCw size={16} /> Tentar novamente</button><Link className="btn secondary" to="/service-level/dashboard"><ArrowLeft size={16} /> Voltar ao fechamento</Link></div></div>;
  if (!data) return <div className="card state-panel" role="status" aria-live="polite"><p className="empty">Carregando detalhes do pedido…</p></div>;

  const { pedido: p, seller, lojista, sla, itens } = data;
  const totQtd = itens.reduce((s, i) => s + i.qtd_comprada, 0);
  const totEntregue = itens.reduce((s, i) => s + i.qtd_entregue, 0);
  const totNf = itens.reduce((s, i) => s + i.valor_nf_item, 0);
  const totCorte = itens.reduce((s, i) => s + i.corte_item, 0);
  const totComprado = itens.reduce((s, i) => s + i.preco_compra_unit * i.qtd_comprada, 0);

  const exportar = () => {
    setErroExportacao('');
    try {
      downloadCsv(
      `pedido_${p.pedido_id}.csv`,
      ['EAN', 'Produto', 'Indústria', 'BU', 'Qtd comprada', 'Qtd entregue', '% atendimento', 'Preço compra (R$)', 'Valor NF (R$)', 'Corte (R$)'],
      itens.map((i) => [
        i.ean, i.descricao_produto, i.industria_nome ?? '', i.bu_sigla ?? '', i.qtd_comprada, i.qtd_entregue,
        i.qtd_comprada > 0 ? ((i.qtd_entregue / i.qtd_comprada) * 100).toFixed(1) : '',
        i.preco_compra_unit.toFixed(2), i.valor_nf_item.toFixed(2), i.corte_item.toFixed(2),
      ])
      );
      void recordServiceLevelActivity('pedido_export', {success: true});
      trackServiceLevelOutcome('pedido_export', true);
    } catch (e) {
      void recordServiceLevelActivity('pedido_export', {success: false});
      trackServiceLevelOutcome('pedido_export', false);
      setErroExportacao(e instanceof Error ? e.message : 'Não foi possível exportar o pedido. Tente novamente.');
    }
  };

  return (
    <>
      <nav className="breadcrumb-nav" aria-label="Navegação estrutural">
        <Link to="/service-level/dashboard">Fechamento</Link><ChevronRight size={14} aria-hidden="true" /><Link to={`/service-level/sellers/${seller.seller_id}`}>Seller</Link><ChevronRight size={14} aria-hidden="true" /><span aria-current="page">Pedido</span>
      </nav>
      <div className="page-header">
        <div>
          <button type="button" className="btn secondary mb-2" onClick={voltar}><ArrowLeft size={14} /> Voltar</button>
          <div className="eyebrow">Acompanhamento de entrega</div>
          <h1>Pedido {p.pedido_id}</h1>
          <div className="sub">
            <Link to={`/service-level/sellers/${seller.seller_id}`}>{seller.nome_fantasia}</Link> · {p.regiao}
          </div>
          {data.visao_restrita && (
            <div className="mt-2">
              <span className="seller-chip" title="Seu perfil vê apenas os itens e valores das indústrias sob sua responsabilidade">
                Visão restrita: {data.visao_restrita.join(', ')}
              </span>
            </div>
          )}
        </div>
        <div className="filters">
          <span className="foto-badge">Última atualização {fmtDataHoraFoto(data.ts_foto)}</span>
          <button type="button" className="btn" data-analytics-feature='Nível de Serviço · Exportar pedido' onClick={exportar} disabled={itens.length === 0}><Download size={16} /> Exportar pedido</button>
        </div>
      </div>

      {erroExportacao && <div className="inline-alert" role="alert">{erroExportacao}</div>}
      <div className="card">
        <div className="section-heading"><div><h2>Informações do pedido</h2><p>Dados do lojista, etapas da entrega e prazo aplicável.</p></div><span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span></div>
        <div className="pedido-header">
          <div className="field"><div className="fl">Lojista</div><div className="fv">{lojista.razao_social}</div>
            <div className="muted text-xs">{lojista.cnpj} · {lojista.cidade}/{lojista.uf}</div></div>
          <div className="field"><div className="fl">Status</div>
            <div className="fv"><span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span></div></div>
          <div className="field"><div className="fl">Data do pedido</div><div className="fv">{fmtDataHora(p.data_pedido)}</div></div>
          <div className="field"><div className="fl">Recebimento</div><div className="fv">{fmtDataHora(p.data_recebimento)}</div></div>
          <div className="field"><div className="fl">Envio</div><div className="fv">{p.data_envio ? fmtDataHora(p.data_envio) : '— não enviado'}</div></div>
          <div className="field"><div className="fl">Finalização</div><div className="fv">{p.data_finalizacao ? fmtDataHora(p.data_finalizacao) : '—'}</div></div>
          <div className="field"><div className="fl">SLA aplicável</div>
            <div className="fv">
              {sla.prazo_horas / 24} {sla.prazo_horas === 24 ? 'dia' : 'dias'} {sla.tipo_contagem === 'UTEIS' ? 'úteis' : 'corridos'}{' '}
              {!sla.configurado && <span className="badge default-sla">SLA não configurado — default</span>}
            </div></div>
          <div className="field"><div className="fl">Tempo em atraso</div>
            <div className={`fv ${p.flag_p1 ? 'text-critical' : ''}`}>
              {p.flag_p1 ? fmtDias(p.horas_em_atraso) : '—'}
            </div></div>
        </div>
        <div className="mt-3.5 flex gap-1.5 flex-wrap">
          {p.flag_p1 && <span className="badge p1">P1 · Pedido parado/atrasado</span>}
          {p.flag_p2 && <span className="badge p2">P2 · Entrega parcial ({fmtPct(p.pct_atendimento_qtd, 0)} atendido)</span>}
          {p.flag_p3 && <span className="badge p3">P3 · Corte de {fmtMoney2(p.valor_corte)}</span>}
        </div>
      </div>

      <div className="card">
        <div className="section-heading"><div><h2>Itens do pedido</h2><p>Compare as quantidades compradas e entregues, o valor faturado e o corte por produto.</p></div><span className="badge perfil">{fmtNum(itens.length)} {itens.length === 1 ? 'item' : 'itens'}</span></div>
        <dl className="detail-metrics detail-totals">
          <div className="detail-metric"><dt>Valor comprado</dt><dd>{fmtMoney2(totComprado)}</dd></div>
          <div className="detail-metric"><dt>Valor faturado</dt><dd>{fmtMoney2(totNf)}</dd></div>
          <div className="detail-metric"><dt>Corte total</dt><dd className={totCorte > 0 ? 'text-critical' : ''}>{fmtMoney2(totCorte)}</dd></div>
          <div className="detail-metric"><dt>Qtd comprada</dt><dd>{fmtNum(totQtd)}</dd></div>
          <div className="detail-metric"><dt>Qtd entregue</dt><dd>{fmtNum(totEntregue)}</dd></div>
          <div className="detail-metric"><dt>Atendimento</dt><dd>{fmtPct(totQtd > 0 ? totEntregue / totQtd : null, 0)}</dd></div>
        </dl>
        {data.visao_restrita && <p className="chart-note">Os itens e totais exibidos consideram as indústrias disponíveis para o seu perfil.</p>}
        <div className="desktop-only table-wrap">
          <table className="min-w-[820px]">
            <thead>
              <tr>
                <th>EAN</th><th>Produto</th><th>Indústria · BU</th>
                <th className="num">Qtd comprada</th><th className="num">Qtd entregue</th>
                <th className="num">% atend.</th><th className="num">Preço compra</th>
                <th className="num">Valor NF</th><th className="num">Corte</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => {
                const pct = i.qtd_comprada > 0 ? i.qtd_entregue / i.qtd_comprada : null;
                return (
                  <tr key={`${i.ean}-${i.industria_id}-${i.bu_id}`}>
                    <td className="muted">{i.ean}</td>
                    <td>{i.descricao_produto}</td>
                    <td className="muted">{i.industria_nome}{i.bu_sigla ? ` · ${i.bu_sigla}` : ''}</td>
                    <td className="num">{fmtNum(i.qtd_comprada)}</td>
                    <td className={`num ${i.qtd_entregue < i.qtd_comprada ? 'text-critical' : ''}`}>
                      {fmtNum(i.qtd_entregue)}
                    </td>
                    <td className="num">{fmtPct(pct, 0)}</td>
                    <td className="num">{fmtMoney2(i.preco_compra_unit)}</td>
                    <td className="num">{fmtMoney2(i.valor_nf_item)}</td>
                    <td className={`num ${i.corte_item > 0 ? 'text-critical' : ''}`}>
                      {i.corte_item > 0 ? fmtMoney2(i.corte_item) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td colSpan={3}>Total · {fmtMoney2(totComprado)} comprados</td>
                <td className="num">{fmtNum(totQtd)}</td>
                <td className="num">{fmtNum(totEntregue)}</td>
                <td className="num">{fmtPct(totQtd > 0 ? totEntregue / totQtd : null, 0)}</td>
                <td className="num" />
                <td className="num">{fmtMoney2(totNf)}</td>
                <td className={`num ${totCorte > 0 ? 'text-critical' : ''}`}>{fmtMoney2(totCorte)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mobile-only">
          {itens.map((i) => {
            const pct = i.qtd_comprada > 0 ? i.qtd_entregue / i.qtd_comprada : null;
            return <article key={`${i.ean}-${i.industria_id}-${i.bu_id}`} className="item-card">
              <div className="item-card-header"><div><h3>{i.descricao_produto}</h3><p>EAN {i.ean}</p><p>{i.industria_nome || 'Indústria não informada'}{i.bu_sigla ? ` · ${i.bu_sigla}` : ''}</p></div><span className={`badge ${i.qtd_entregue < i.qtd_comprada ? 'p2' : 'FINALIZADO'}`}>{fmtPct(pct, 0)} atendido</span></div>
              <dl className="detail-metrics">
                <div className="detail-metric"><dt>Qtd comprada</dt><dd>{fmtNum(i.qtd_comprada)}</dd></div>
                <div className="detail-metric"><dt>Qtd entregue</dt><dd className={i.qtd_entregue < i.qtd_comprada ? 'text-critical' : ''}>{fmtNum(i.qtd_entregue)}</dd></div>
                <div className="detail-metric"><dt>Preço unitário</dt><dd>{fmtMoney2(i.preco_compra_unit)}</dd></div>
                <div className="detail-metric"><dt>Valor da NF</dt><dd>{fmtMoney2(i.valor_nf_item)}</dd></div>
                <div className="detail-metric"><dt>Corte</dt><dd className={i.corte_item > 0 ? 'text-critical' : ''}>{i.corte_item > 0 ? fmtMoney2(i.corte_item) : '—'}</dd></div>
                <div className="detail-metric"><dt>Atendimento</dt><dd>{fmtPct(pct, 0)}</dd></div>
              </dl>
            </article>;
          })}
        </div>
        {itens.length === 0 && <p className="empty">Nenhum item disponível para este pedido.</p>}
      </div>
    </>
  );
}
